import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCheck,
	faChevronDown,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import { generalStyles } from "../../styles/styles"
import { COLORS } from "../../../utilities/Constants"

// Padding du .card (cf. generalStyles.card) — réutilisé pour étendre la zone
// cliquable de l'en-tête jusqu'aux bords sans déplacer le contenu.
const CARD_PAD_Y = 20
const CARD_PAD_X = 24

// Pastille de statut (commun complété / incomplet).
const badgeStyle = (color, bg) => ({
	display: "inline-flex",
	alignItems: "center",
	gap: 5,
	padding: "2px 9px",
	borderRadius: 99,
	fontSize: 11,
	fontWeight: 600,
	color,
	backgroundColor: bg,
	border: `1px solid ${color}33`,
	textTransform: "none",
	letterSpacing: 0,
})

/**
 * Carte repliable V2 (remplace le legacy CollapsibleComponent).
 *
 * - En-tête cliquable (ouvre/ferme) avec chevron. La zone cliquable couvre toute
 *   la largeur/hauteur de l'en-tête, padding du `.card` inclus.
 * - Statut : `status` = "complete" | "incomplete" | null (neutre). Rétro-compat :
 *   `completed` (booléen) → "complete" si vrai. Complété = badge vert + liseré vert ;
 *   incomplet = badge ambre + liseré ambre ; neutre = aucun.
 * - Hauteur d'en-tête constante (`minHeight`) quel que soit l'état : les cartes
 *   placées côte à côte (flex) restent alignées, badge ou non.
 * - `right` : nœud optionnel aligné à droite de l'en-tête (ex. bouton d'action) ;
 *   son clic ne déclenche pas le repli (stopPropagation).
 */
const CollapsibleCardV2 = ({
	title,
	completed = false,
	status = null,
	defaultOpen = true,
	right = null,
	children,
	style = null,
	// Mode contrôlé optionnel : si `open` est fourni, l'état est piloté par le
	// parent (`onToggle`) — sert au repli partagé par ligne de cartes.
	open: openProp,
	onToggle,
}) => {
	const [openState, setOpenState] = useState(defaultOpen)
	const controlled = openProp !== undefined
	const open = controlled ? openProp : openState
	const toggle = () =>
		controlled ? onToggle && onToggle() : setOpenState((o) => !o)

	const state = status || (completed ? "complete" : null)
	const accent =
		state === "complete"
			? COLORS.success
			: state === "incomplete"
				? COLORS.warning
				: null

	return (
		<div
			style={{
				...generalStyles.card,
				marginBottom: 16,
				...(accent ? { borderLeft: `3px solid ${accent}` } : {}),
				...style,
			}}
		>
			<div
				onClick={toggle}
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					userSelect: "none",
					// Hauteur constante (badge ou non) pour aligner les cartes.
					minHeight: 22,
					boxSizing: "content-box",
					// Étend la zone cliquable jusqu'aux bords du .card en annulant son
					// padding par une marge négative, puis en le ré-appliquant : le
					// contenu ne bouge pas, seul le hit-area grandit.
					margin: open
						? `-${CARD_PAD_Y}px -${CARD_PAD_X}px 18px`
						: `-${CARD_PAD_Y}px -${CARD_PAD_X}px`,
					padding: open
						? `${CARD_PAD_Y}px ${CARD_PAD_X}px 12px`
						: `${CARD_PAD_Y}px ${CARD_PAD_X}px`,
					borderBottom: open
						? `1px solid ${COLORS.border}`
						: "none",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
					<FontAwesomeIcon
						icon={open ? faChevronDown : faChevronRight}
						style={{ fontSize: 11, color: COLORS.textLight }}
					/>
					<span
						style={{
							fontSize: 11,
							fontWeight: 600,
							color: COLORS.textLight,
							letterSpacing: "0.1em",
							textTransform: "uppercase",
						}}
					>
						{title}
					</span>
					{state === "complete" && (
						<span
							style={badgeStyle(
								COLORS.success,
								COLORS.successLight,
							)}
						>
							<FontAwesomeIcon
								icon={faCheck}
								style={{ fontSize: 10 }}
							/>
							Complété
						</span>
					)}
					{state === "incomplete" && (
						<span
							style={badgeStyle(
								COLORS.warning,
								COLORS.warningLight,
							)}
						>
							<span
								style={{
									width: 7,
									height: 7,
									borderRadius: "50%",
									border: `1.5px solid ${COLORS.warning}`,
								}}
							/>
							Incomplet
						</span>
					)}
				</div>
				{right && (
					<div onClick={(e) => e.stopPropagation()}>{right}</div>
				)}
			</div>
			{open && <div>{children}</div>}
		</div>
	)
}

export default CollapsibleCardV2
